import { useState, useEffect, useCallback } from 'react'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { Box, Typography, CircularProgress } from '@mui/material'
import { supabase } from '../../utils/supabase'
import { uploadPetAvatar } from '../../utils/petPhotoUtils'
import {
	canEmployeePerformService,
	convertToUtcTimestamps,
	getAppointmentDuration,
	SLOT_INTERVAL_MINUTES
} from '../../utils/appointmentUtils'
import { parseAddOns } from '../../utils/checkoutUtils'
import AddEditClientDialog from '../AddEditClientDialog'
import AddEditPetPage from '../AddEditPetPage'
import { getFullName } from '../../utils/clientUtils'
import DropdownCalendar from '../ui/DropdownCalendar'
import TimeListPicker from '../ui/TimeListPicker'
import OverlayScrollThumb from '../ui/OverlayScrollThumb'
import {
	L,
	Icon,
	LavSectionTitle,
	LavDropdown,
	LavAddonField,
} from './appointmentLavenderFields'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * AddSingleAppointment - Form for adding a single pet's appointment
 *
 * @param {object} merchant - Merchant object
 * @param {boolean} isFirstPet - Whether this is the first pet (shows date/time/client pickers)
 * @param {object} selectedClient - Pre-selected client (locked after first pet)
 * @param {string} selectedDate - Pre-selected date (locked after first pet)
 * @param {string} selectedTime - Pre-selected time
 * @param {array} existingPetIds - Pet IDs already added (to filter from dropdown)
 * @param {object} editingAppointment - If editing an existing appointment
 * @param {function} onSave - Callback with appointment data
 * @param {function} onCancel - Callback to cancel
 * @param {boolean} isMobile - Mobile view flag
 */
export default function AddSingleAppointment({
	merchant,
	isFirstPet = true,
	selectedClient: preselectedClient = null,
	selectedDate: preselectedDate = null,
	selectedTime: preselectedTime = null,
	// AI prefill — seeded as initial state and applied via useEffect when the
	// dialog opens with a fresh suggestion. Each is an object (not just an id)
	// so the form can render immediately without waiting on the data load.
	preselectedPet = null,
	preselectedService = null,
	preselectedAddOns = null,
	preselectedEmployee = null,
	// Multi-pet visits share one employee. When set (adding/editing any pet
	// after the first), the employee field is locked to this value.
	lockedEmployee = null,
	existingPetIds = [],
	existingAppointmentsDuration = 0, // Total duration of existing appointments (for "Ends at" calculation)
	editingAppointment = null,
	excludeAppointmentIds = [], // IDs of all appointments being edited (to exclude from overlap check)
	onSave,
	onCancel,
	isMobile = false,
	saveActionRef = null
}) {
	// Inject Outfit font (lavender design typeface)
	useEffect(() => {
		const id = 'outfit-font-link'
		if (!document.getElementById(id)) {
			const link = document.createElement('link')
			link.id = id
			link.rel = 'stylesheet'
			link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap'
			document.head.appendChild(link)
		}
	}, [])

	// Loading and error state
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [saving, setSaving] = useState(false)

	// Data state
	const [services, setServices] = useState([])
	const [clients, setClients] = useState([])
	const [employees, setEmployees] = useState([])
	const [addOns, setAddOns] = useState([])

	// Form state
	const [selectedClient, setSelectedClient] = useState(preselectedClient)
	const [selectedDate, setSelectedDate] = useState(preselectedDate || dayjs().format('YYYY-MM-DD'))
	const [selectedTime, setSelectedTime] = useState(preselectedTime || '')
	const [selectedPet, setSelectedPet] = useState(preselectedPet)
	const [clientPets, setClientPets] = useState([])
	const [loadingPets, setLoadingPets] = useState(false)
	const [selectedService, setSelectedService] = useState(preselectedService)
	const [selectedAddOns, setSelectedAddOns] = useState(preselectedAddOns || [])
	const [selectedEmployee, setSelectedEmployee] = useState(lockedEmployee || preselectedEmployee)
	const [availableEmployees, setAvailableEmployees] = useState([])

	// Keep the selection pinned to the group's employee while locked (covers
	// the editing-appointment effect re-seeding from the stored row).
	useEffect(() => {
		if (lockedEmployee) setSelectedEmployee(lockedEmployee)
	}, [lockedEmployee])

	// The locked employee may not offer the chosen service — surface it instead
	// of letting Save produce a booking the employee can't perform.
	const lockedEmployeeConflict = Boolean(
		lockedEmployee && selectedService &&
		!canEmployeePerformService(lockedEmployee, selectedService.id)
	)


	// Dialog state
	const [createClientDialogOpen, setCreateClientDialogOpen] = useState(false)
	const [createPetDialogOpen, setCreatePetDialogOpen] = useState(false)

	// Overlap warning state (allows overlapping appointments with warning)
	const [overlapWarning, setOverlapWarning] = useState(null)

	// Load initial data
	const loadInitialData = useCallback(async () => {
		if (!merchant?.id) return

		setLoading(true)
		setError('')
		try {
			// Load services (bookable services only)
			const { data: servicesData, error: servicesError } = await supabase
				.from('service_types')
				.select('id, name, duration_minutes, category, appointment_color, price')
				.eq('merchant_id', merchant.id)
				.eq('is_service', true)
				.order('name')

			if (servicesError) throw servicesError
			setServices(servicesData || [])

			// Load add-ons
			const { data: addOnsData, error: addOnsError } = await supabase
				.from('service_types')
				.select('id, name, price, duration_minutes')
				.eq('merchant_id', merchant.id)
				.eq('is_add_on', true)
				.order('name')

			if (addOnsError) throw addOnsError
			setAddOns(addOnsData || [])

			// Load clients
			const { data: clientsData, error: clientsError } = await supabase
				.from('clients')
				.select('id, name, last_name, email, phone_number')
				.eq('merchant_id', merchant.id)
				.order('name')

			if (clientsError) throw clientsError
			setClients(clientsData || [])

			// Load employees
			const { data: employeesData, error: employeesError } = await supabase
				.from('employees')
				.select('id, first_name, last_name, services')
				.eq('merchant_id', merchant.id)
				.eq('active', true)
				.order('first_name')

			if (employeesError) throw employeesError
			setEmployees(employeesData || [])

		} catch (e) {
			console.error('Error loading data:', e)
			setError('Failed to load data')
		} finally {
			setLoading(false)
		}
	}, [merchant?.id])

	useEffect(() => {
		loadInitialData()
	}, [loadInitialData])

	// Update selected client when preselected changes
	useEffect(() => {
		if (preselectedClient && !selectedClient) {
			setSelectedClient(preselectedClient)
		}
	}, [preselectedClient, selectedClient])

	// Update date/time when preselected changes (e.g. user taps a calendar slot while dialog is open)
	useEffect(() => {
		if (preselectedDate) setSelectedDate(preselectedDate)
		if (preselectedTime) setSelectedTime(preselectedTime)
	}, [preselectedDate, preselectedTime])

	// AI prefill: apply suggested pet/service/add-ons/employee whenever a new
	// suggestion arrives. We only seed when a value is provided (so the merchant
	// can still freely edit afterwards without re-running effects).
	useEffect(() => {
		if (preselectedPet) setSelectedPet(preselectedPet)
	}, [preselectedPet])
	useEffect(() => {
		if (preselectedService) setSelectedService(preselectedService)
	}, [preselectedService])
	useEffect(() => {
		if (preselectedAddOns) setSelectedAddOns(preselectedAddOns)
	}, [preselectedAddOns])
	useEffect(() => {
		if (preselectedEmployee) setSelectedEmployee(preselectedEmployee)
	}, [preselectedEmployee])
	// Load editing appointment data
	useEffect(() => {
		if (editingAppointment) {
			setSelectedPet(editingAppointment.pet)
			setSelectedService(editingAppointment.service)
			setSelectedAddOns(editingAppointment.addOns || [])
			setSelectedEmployee(lockedEmployee || editingAppointment.employee)
		}
	}, [editingAppointment, lockedEmployee])

	// Load pets when client changes
	useEffect(() => {
		const loadPets = async () => {
			if (!selectedClient?.id) {
				setClientPets([])
				setSelectedPet(null)
				return
			}

			setLoadingPets(true)
			try {
				const { data, error } = await supabase
					.from('pets')
					.select('id, name, species, breed, weight')
					.eq('client_id', selectedClient.id)
					.order('name')

				if (error) throw error

				// Filter out already added pets
				const filteredPets = (data || []).filter(p => !existingPetIds.includes(p.id))
				setClientPets(filteredPets)

				// Auto-select first pet if only one available — but not if we
				// already have a preselected pet (from AI prefill or editing).
				if (filteredPets.length === 1 && !editingAppointment && !preselectedPet) {
					setSelectedPet(filteredPets[0])
				}
			} catch (e) {
				console.error('Error loading pets:', e)
			} finally {
				setLoadingPets(false)
			}
		}

		loadPets()
	}, [selectedClient?.id, existingPetIds, editingAppointment, preselectedPet])

	// When a pet is picked, look up their most recent appointment so the
	// service and add-ons they had last time float to the top of the pickers.
	// Skipped while editing — the "last" appointment there is usually the one
	// being edited. Purely a convenience; failures just mean no reordering.
	const [lastUsed, setLastUsed] = useState(null) // { petId, serviceId, addOnIds } | null
	useEffect(() => {
		if (!selectedPet?.id || !merchant?.id || editingAppointment) {
			setLastUsed(null)
			return
		}
		let cancelled = false
		// Most recently CREATED appointment (not latest by schedule date) — it
		// best reflects the merchant's latest booking choice for this pet, even
		// when a far-future appointment already exists on the calendar.
		supabase
			.from('appointments')
			.select('service_id, add_ons, created_at')
			.eq('merchant_id', merchant.id)
			.eq('pet_id', selectedPet.id)
			.not('status', 'eq', 'cancelled')
			.order('created_at', { ascending: false })
			.limit(1)
			.then(({ data, error }) => {
				if (cancelled) return
				const last = data?.[0]
				if (error || !last) {
					if (error) console.warn('Could not load last appointment for pet:', error)
					setLastUsed(null)
					return
				}
				setLastUsed({
					petId: selectedPet.id,
					serviceId: last.service_id || null,
					addOnIds: [...new Set(parseAddOns(last.add_ons).map(a => a.service_id).filter(Boolean))],
				})
			})
		return () => { cancelled = true }
	}, [selectedPet?.id, merchant?.id, editingAppointment])

	// Update available employees when service changes
	// Note: We show all employees who can perform the service, regardless of time conflicts
	// This allows merchants to create overlapping appointments when needed
	useEffect(() => {
		if (!selectedService) {
			setAvailableEmployees([])
			return
		}

		// Filter by service capability only (not by availability)
		const serviceCapable = employees.filter(emp =>
			canEmployeePerformService(emp, selectedService.id)
		)
		setAvailableEmployees(serviceCapable)

		// Locked to the group's employee — never auto-swap or clear; a
		// capability mismatch is surfaced as lockedEmployeeConflict instead.
		if (lockedEmployee) return

		// Auto-select if only one employee available
		if (serviceCapable.length === 1 && !selectedEmployee) {
			setSelectedEmployee(serviceCapable[0])
		} else if (selectedEmployee && employees.length > 0 && !serviceCapable.find(e => e.id === selectedEmployee.id)) {
			// Clear employee if they can't perform the new service
			// Only clear if employees have loaded (employees.length > 0) to avoid race condition
			// Don't clear if this is the employee from editingAppointment
			const isEditingEmployee = editingAppointment?.employee?.id === selectedEmployee.id
			if (!isEditingEmployee) {
				setSelectedEmployee(null)
			}
		}
	}, [selectedService, employees, selectedEmployee, editingAppointment, lockedEmployee])

	// Check for overlap warning when employee, date, or time changes
	useEffect(() => {
		const checkOverlap = async () => {
			if (!selectedEmployee || !selectedDate || !selectedTime || !selectedService || !merchant) {
				setOverlapWarning(null)
				return
			}

			try {
				// Calculate total duration including add-ons
				let duration = selectedService.duration_minutes || 0
				duration += selectedAddOns.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0)

				const { utcStart, utcEnd } = convertToUtcTimestamps(
					selectedDate,
					selectedTime,
					merchant.timezone,
					duration
				)

				// Check for existing appointments that overlap
				const { data: existingAppointments, error } = await supabase
					.from('appointments')
					.select('id, start_ts, end_ts, service_id, customer_name')
					.eq('merchant_id', merchant.id)
					.eq('employee_id', selectedEmployee.id)
					.eq('status', 'booked')
					.lt('start_ts', utcEnd)
					.gt('end_ts', utcStart)

				if (error) {
					console.error('Error checking overlap:', error)
					setOverlapWarning(null)
					return
				}

				// Filter out all appointments being edited
				// Include both the single editingAppointment ID and any IDs in excludeAppointmentIds
				const idsToExclude = new Set([
					...(editingAppointment?.id ? [editingAppointment.id] : []),
					...excludeAppointmentIds.filter(Boolean)
				])
				const relevantAppointments = idsToExclude.size > 0
					? (existingAppointments || []).filter(apt => !idsToExclude.has(apt.id))
					: existingAppointments || []

				if (relevantAppointments.length > 0) {
					// Format the overlapping appointments info
					const overlapDetails = relevantAppointments.map(apt => {
						const startTime = dayjs(apt.start_ts).tz(merchant.timezone).format('h:mm A')
						const endTime = dayjs(apt.end_ts).tz(merchant.timezone).format('h:mm A')
						return `${apt.customer_name || 'Unknown'} (${startTime} - ${endTime})`
					}).join(', ')

					setOverlapWarning({
						count: relevantAppointments.length,
						details: overlapDetails,
						employeeName: `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
					})
				} else {
					setOverlapWarning(null)
				}
			} catch (e) {
				console.error('Error checking overlap:', e)
				setOverlapWarning(null)
			}
		}

		checkOverlap()
	}, [selectedEmployee, selectedDate, selectedTime, selectedService, merchant, selectedAddOns, editingAppointment, excludeAppointmentIds])

	// Handle client created
	const handleClientCreated = (result) => {
		// onClientSaved returns { client, pets }
		const newClient = result?.client || result
		setClients(prev => [...prev, newClient])
		setSelectedClient(newClient)
		setSelectedPet(null)
		setCreateClientDialogOpen(false)
	}

	// Handle pet created - save to database
	const handlePetCreated = async (petData) => {
		try {
			// Extract pending avatar blob if present
			const pendingAvatarBlob = petData._pendingAvatarBlob
			delete petData._pendingAvatarBlob

			// Insert pet into database
			const { data: savedPet, error } = await supabase
				.from('pets')
				.insert({
					...petData,
					client_id: selectedClient.id
				})
				.select()
				.single()

			if (error) throw error

			// Upload pending avatar if present
			if (pendingAvatarBlob && savedPet && merchant?.id) {
				try {
					await uploadPetAvatar(
						savedPet.id,
						merchant.id,
						selectedClient.id,
						pendingAvatarBlob,
						null // No existing photo for new uploads
					)
				} catch (avatarError) {
					console.error('Error uploading avatar:', avatarError)
					// Don't fail the save if avatar upload fails
				}
			}

			// Update local state with saved pet
			setClientPets(prev => [...prev, savedPet])
			setSelectedPet(savedPet)
			setCreatePetDialogOpen(false)
		} catch (error) {
			console.error('Error saving pet:', error)
			setError('Failed to save pet')
		}
	}

	// Currency formatting
	const cs = getCurrencySymbol(merchant?.currency)
	const formatCurrency = (cents) => `${cs}${(cents / 100).toFixed(2)}`

	// Add-on toggle (LavAddonField emits the toggled id)
	const toggleAddOn = (id) => {
		const exists = selectedAddOns.find(a => a.id === id)
		if (exists) {
			setSelectedAddOns(selectedAddOns.filter(a => a.id !== id))
		} else {
			const addon = addOns.find(a => a.id === id)
			if (addon) setSelectedAddOns([...selectedAddOns, addon])
		}
	}

	const addOnsTotal = selectedAddOns.reduce((sum, a) => sum + (a.price || 0), 0)

	// Dropdown option shapes
	const clientOptions = [
		...clients.map(c => ({
			id: c.id,
			label: getFullName(c),
			sub: c.email || c.phone_number || '',
		})),
		{ id: 'create-new-client', label: '+ Create New Client', isCreateOption: true },
	]

	const petOptions = [
		...clientPets.map(p => ({
			id: p.id,
			label: p.name,
			sub: [p.breed, p.species].filter(Boolean).join(' | '),
		})),
		...(selectedClient ? [{ id: 'create-new-pet', label: '+ Create New Pet', isCreateOption: true }] : []),
	]

	// Float the pet's last-used service and add-ons to the top of their
	// pickers. Guard on petId so a stale fetch for a previously selected pet
	// never reorders the current one's lists.
	const lastForPet = lastUsed && lastUsed.petId === selectedPet?.id ? lastUsed : null
	const lastServiceId = lastForPet?.serviceId || null
	const lastAddOnIds = lastForPet?.addOnIds || []

	const orderedServices = lastServiceId
		? [...services.filter(s => s.id === lastServiceId), ...services.filter(s => s.id !== lastServiceId)]
		: services
	const orderedAddOns = lastAddOnIds.length > 0
		? [
			...addOns.filter(a => lastAddOnIds.includes(a.id)),
			...addOns.filter(a => !lastAddOnIds.includes(a.id)),
		]
		: addOns

	const serviceOptions = orderedServices.map(s => ({
		id: s.id,
		label: s.name,
		meta: `${formatCurrency(s.price)} · ${s.duration_minutes}m`,
	}))

	const employeeOptions = availableEmployees.map(e => ({
		id: e.id,
		label: `${e.first_name} ${e.last_name}`,
	}))

	// Calculate end time (includes existing appointments' duration for grouped bookings)
	const calculateEndTime = () => {
		if (!selectedTime || !selectedService) return null

		// If editing an existing appointment with timestamps, use getAppointmentDuration
		// to get the actual stored duration, otherwise calculate from service + addOns
		let duration
		if (editingAppointment?.start_ts && editingAppointment?.end_ts) {
			// Use the actual duration from the editing appointment
			duration = getAppointmentDuration(editingAppointment)
		} else {
			// Calculate from selected service and add-ons (for new appointments)
			duration = selectedService.duration_minutes || 0
			duration += selectedAddOns.reduce((sum, addon) => sum + (addon.duration_minutes || 0), 0)
		}

		// Add existing appointments' duration to get the actual end time
		const totalDuration = existingAppointmentsDuration + duration

		const startTime = dayjs(`2000-01-01 ${selectedTime}`)
		return startTime.add(totalDuration, 'minute').format('h:mm A')
	}

	// Handle save
	// Build the appointment payload after validating required fields. Returns
	// null and sets an error if anything is missing.
	const buildAppointmentData = () => {
		if (!selectedPet || !selectedService) {
			setError('Please select a pet and service')
			return null
		}
		if (!selectedEmployee) {
			setError('Please select an employee')
			return null
		}
		if (lockedEmployeeConflict) {
			setError(`${lockedEmployee.first_name} doesn’t offer ${selectedService.name} — the whole visit is booked with one employee.`)
			return null
		}
		if (isFirstPet && (!selectedClient || !selectedDate || !selectedTime)) {
			setError('Please fill in all required fields')
			return null
		}
		return {
			pet: selectedPet,
			service: selectedService,
			addOns: selectedAddOns,
			employee: lockedEmployee || selectedEmployee,
			...(isFirstPet && {
				client: selectedClient,
				date: selectedDate,
				time: selectedTime
			})
		}
	}

	const handleSave = () => {
		const appointmentData = buildAppointmentData()
		if (!appointmentData) return
		setSaving(true)
		onSave(appointmentData)
		setSaving(false)
	}

	// Check if form is valid
	const isValid = selectedPet && selectedService && selectedEmployee && !lockedEmployeeConflict &&
		(isFirstPet ? (selectedClient && selectedDate && selectedTime) : true)

	// Register save action for mobile header button
	useEffect(() => {
		if (saveActionRef) {
			saveActionRef.current = handleSave
		}
		return () => {
			if (saveActionRef) {
				saveActionRef.current = null
			}
		}
	})

	// Touch-sized read-only fields on mobile (design `.mbap .ap-input`).
	const roSx = {
		...readonlyFieldSx,
		...(isMobile ? { height: '48px', borderRadius: '12px', px: '13px' } : {}),
	}
	const roTextSx = { fontSize: isMobile ? '15px' : '13.5px', fontWeight: 600, color: L.text }

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
				<CircularProgress sx={{ color: L.primary }} />
			</Box>
		)
	}

	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			height: '100%',
			overflow: 'hidden',
			fontFamily: L.font,
		}}>
			{/* Scrollable Content — hidden while the create-pet form takes over
			    (the pet form renders as a full-screen overlay on both form factors). */}
			<Box className="lav-overlay-scroll" sx={{
				display: createPetDialogOpen ? 'none' : 'flex',
				flexDirection: 'column',
				gap: '18px',
				flex: 1,
				overflow: 'auto',
				p: '4px 16px 18px'
			}}>
				<OverlayScrollThumb />
				{error && (
					<Box sx={{
						display: 'flex', alignItems: 'center', gap: '8px', p: '11px 13px',
						bgcolor: '#FFF0F0', border: '1px solid #FECACA', borderRadius: L.rSm,
						color: '#8B1A1A', fontSize: '13px', fontFamily: L.font, cursor: 'pointer',
					}} onClick={() => setError('')}>
						<Box sx={{ flex: 'none', display: 'flex', color: '#EF4444' }}><Icon name="warn" size={15} /></Box>
						<Box sx={{ flex: 1 }}>{error}</Box>
						<Box sx={{ flex: 'none', display: 'flex', color: '#8B1A1A' }}><Icon name="x" size={13} /></Box>
					</Box>
				)}

				{/* When Section - Only for first pet */}
				{isFirstPet && (
					<Box component="section">
						<LavSectionTitle>When</LavSectionTitle>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1.15fr 1fr', gap: '10px' }}>
							<DropdownCalendar
								value={selectedDate}
								onChange={setSelectedDate}
								min={dayjs().format('YYYY-MM-DD')}
								placeholder="Pick a date"
							/>
							<TimeListPicker
								value={selectedTime}
								onChange={setSelectedTime}
								step={SLOT_INTERVAL_MINUTES}
								align="right"
							/>
						</Box>

					</Box>
				)}

				{/* Client details */}
				<Box component="section">
					<LavSectionTitle>Client details</LavSectionTitle>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
						{isFirstPet ? (
							preselectedClient ? (
								<Box sx={roSx}>
									<Box sx={{ display: 'flex', color: L.muted, flex: 'none' }}><Icon name="user" size={15} /></Box>
									<Box component="span" sx={roTextSx}>
										{getFullName(selectedClient || preselectedClient)}
									</Box>
								</Box>
							) : (
								<LavDropdown
									icon={<Icon name="user" size={15} />}
									placeholder="Search client by name, email or phone"
									value={selectedClient?.id ?? null}
									options={clientOptions}
									searchable
									onSelect={(id) => {
										if (id === 'create-new-client') { setCreateClientDialogOpen(true); return }
										const c = clients.find(x => x.id === id) || null
										setSelectedClient(c)
										setSelectedPet(null)
									}}
								/>
							)
						) : (
							// Locked client for subsequent pets
							<Box sx={roSx}>
								<Box sx={{ display: 'flex', color: L.muted, flex: 'none' }}><Icon name="user" size={15} /></Box>
								<Box component="span" sx={roTextSx}>
									{getFullName(preselectedClient)}
								</Box>
							</Box>
						)}

						{/* Pet Selector */}
						{loadingPets ? (
							<Box sx={{ ...roSx, bgcolor: L.field }}>
								<Box sx={{ display: 'flex', color: L.muted, flex: 'none' }}><Icon name="paw" size={15} /></Box>
								<CircularProgress size={16} sx={{ color: L.primary }} />
							</Box>
						) : (
							<LavDropdown
								icon={<Icon name="paw" size={15} />}
								placeholder="Pet"
								value={selectedPet?.id ?? null}
								options={petOptions}
								disabled={!selectedClient}
								onSelect={(id) => {
									if (id === 'create-new-pet') { setCreatePetDialogOpen(true); return }
									const p = clientPets.find(x => x.id === id) || null
									setSelectedPet(p)
								}}
							/>
						)}
					</Box>
				</Box>

				{/* Service details */}
				<Box component="section">
					<LavSectionTitle>Service details</LavSectionTitle>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
						<LavDropdown
							placeholder="Select service"
							value={selectedService?.id ?? null}
							options={serviceOptions}
							onSelect={(id) => setSelectedService(services.find(s => s.id === id) || null)}
						/>

						<LavAddonField
							addOns={orderedAddOns}
							selected={selectedAddOns}
							onToggle={toggleAddOn}
							formatCurrency={formatCurrency}
						/>
						{addOnsTotal > 0 && (
							<Box sx={{
								display: 'flex', justifyContent: 'space-between', fontSize: '12.5px',
								fontWeight: 600, color: L.muted, fontFamily: L.font,
							}}>
								<span>Add-ons Total:</span>
								<Box component="strong" sx={{ color: L.text, fontSize: '13px' }}>{formatCurrency(addOnsTotal)}</Box>
							</Box>
						)}

						{lockedEmployee ? (
							// One employee per visit: pets after the first inherit the
							// group's employee (change it on the first pet).
							<Box>
								<Box sx={roSx}>
									<Box sx={{ display: 'flex', color: L.muted, flex: 'none' }}><Icon name="user" size={15} /></Box>
									<Box component="span" sx={roTextSx}>
										{`${lockedEmployee.first_name || ''} ${lockedEmployee.last_name || ''}`.trim()}
									</Box>
									<Box component="span" sx={{
										ml: 'auto', flex: 'none', fontSize: isMobile ? '12px' : '11.5px',
										fontWeight: 650, color: L.muted, fontFamily: L.font,
									}}>
										Same for all pets
									</Box>
								</Box>
								{lockedEmployeeConflict && (
									<Box sx={{
										display: 'flex', alignItems: 'flex-start', gap: '8px', p: '12px', mt: '8px',
										bgcolor: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: L.rSm,
									}}>
										<Box sx={{ flexShrink: 0, mt: '1px', color: '#D97706', display: 'flex' }}>
											<Icon name="warn" size={16} />
										</Box>
										<Typography sx={{
											fontFamily: L.font, fontWeight: 400, fontSize: '12px', lineHeight: 1.4, color: '#B45309',
										}}>
											{`${lockedEmployee.first_name} doesn’t offer ${selectedService?.name}. Pick a different service, or change the employee on the first pet.`}
										</Typography>
									</Box>
								)}
							</Box>
						) : (
							<LavDropdown
								icon={<Icon name="user" size={15} />}
								placeholder="Select employee"
								value={selectedEmployee?.id ?? null}
								options={employeeOptions}
								clearable
								disabled={!selectedService}
								onSelect={(id) => {
									if (id === null) { setSelectedEmployee(null); return }
									setSelectedEmployee(availableEmployees.find(e => e.id === id) || null)
								}}
							/>
						)}

						{/* Overlap Warning — hidden while a recurring series is configured
						    (the series overview performs its own conflict check). */}
						{overlapWarning && (
							<Box sx={{
								display: 'flex', alignItems: 'flex-start', gap: '8px', p: '12px',
								bgcolor: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: L.rSm,
							}}>
								<Box sx={{ flexShrink: 0, mt: '1px', color: '#D97706', display: 'flex' }}>
									<Icon name="warn" size={16} />
								</Box>
								<Box sx={{ flex: 1 }}>
									<Typography sx={{
										fontFamily: L.font, fontWeight: 700, fontSize: '13px', color: '#92400E', mb: '4px',
									}}>
										Overlapping Appointment
									</Typography>
									<Typography sx={{
										fontFamily: L.font, fontWeight: 400, fontSize: '12px', lineHeight: 1.4, color: '#B45309',
									}}>
										{overlapWarning.employeeName} already has {overlapWarning.count} appointment{overlapWarning.count > 1 ? 's' : ''} during this time: {overlapWarning.details}
									</Typography>
								</Box>
							</Box>
						)}
					</Box>
				</Box>

				{/* End time preview */}
				{selectedTime && selectedService && (
					<Box sx={{
						bgcolor: L.surface, borderRadius: isMobile ? '12px' : L.rSm,
						p: isMobile ? '12px 14px' : '11px 13px',
						fontSize: isMobile ? '14px' : '13px', color: L.muted, fontFamily: L.font,
					}}>
						Ends at: <Box component="strong" sx={{ color: L.text, fontWeight: 700 }}>{calculateEndTime()}</Box>
					</Box>
				)}
			</Box>

			{/* Bottom Fixed Section - hide when creating pet.
			    Mobile uses the design's sticky footer (52px buttons, full-width Save). */}
			{!createPetDialogOpen && (
				<Box sx={{
					display: 'flex', gap: '10px', justifyContent: 'flex-end',
					pt: isMobile ? '12px' : '14px',
					pb: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 20px)' : '20px',
					px: '16px',
					borderTop: `1px solid ${L.border}`, mt: 'auto', bgcolor: L.bg,
				}}>
					<Box
						component="button"
						type="button"
						onClick={onCancel}
						disabled={saving}
						sx={{
							px: isMobile ? '22px' : '16px', height: isMobile ? '52px' : '42px',
							borderRadius: isMobile ? '14px' : L.rSm, flex: 'none',
							border: `1.5px solid ${L.border}`, bgcolor: isMobile ? '#FFFFFF' : 'transparent',
							fontSize: isMobile ? '15px' : '14px', fontWeight: 650, color: L.muted, fontFamily: L.font, cursor: 'pointer',
							'&:hover:not(:disabled)': { color: L.text, borderColor: L.muted },
							'&:disabled': { opacity: 0.5, cursor: 'default' },
						}}
					>
						Cancel
					</Box>
					<Box
						component="button"
						type="button"
						onClick={handleSave}
						disabled={!isValid || saving}
						sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
							height: isMobile ? '52px' : '42px', px: '22px',
							borderRadius: isMobile ? '14px' : L.rSm, border: 'none',
							flex: isMobile ? 1 : 'none',
							bgcolor: L.primary, color: L.onPrimary,
							fontSize: isMobile ? '15.5px' : '14px', fontWeight: isMobile ? 750 : 700,
							fontFamily: L.font, whiteSpace: 'nowrap', cursor: 'pointer',
							boxShadow: `0 2px 8px ${L.primarySoft}`,
							'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
							'&:disabled': { opacity: 0.45, cursor: 'default', boxShadow: 'none' },
						}}
					>
						
						<span>{saving ? 'Saving…' : 'Save'}</span>
					</Box>
				</Box>
			)}

			{/* Create Client Dialog */}
			<AddEditClientDialog
				open={createClientDialogOpen}
				onClose={() => setCreateClientDialogOpen(false)}
				merchant={merchant}
				onClientSaved={handleClientCreated}
			/>

			{/* Create Pet Dialog */}
			{/* Create Pet — reuses the compact mobile pet form. On mobile it's a
			    full-screen overlay; on desktop `embedded` renders that same layout
			    in-flow so it stays within the add-appointment panel. */}
			{createPetDialogOpen && selectedClient && (
				// Desktop embedded form keeps the 16px inset the dialog wrapper used
				// to provide (the wrapper is now padding-free so the scroll container
				// can reach the panel edge). Mobile renders a full-screen overlay.
				<Box sx={{
					display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
					overflow: 'hidden', p: isMobile ? 0 : '0 16px 16px',
				}}>
					<AddEditPetPage
						onSave={handlePetCreated}
						onClose={() => setCreatePetDialogOpen(false)}
						merchantId={merchant?.id}
						clientId={selectedClient.id}
						isMobile={isMobile}
						embedded={!isMobile}
					/>
				</Box>
			)}
		</Box>
	)
}

const readonlyFieldSx = {
	display: 'flex', alignItems: 'center', gap: '9px',
	border: `1.5px solid ${L.border}`, borderRadius: L.rSm,
	bgcolor: L.surface, height: '42px', px: '12px',
}
